// RUTA: /src/hooks/useAdminData.js
// Hook para gestionar la carga inicial de datos del panel de administración
// OPTIMIZADO: Lazy loading y cache inteligente

import { useState, useEffect, useRef } from 'react';
import { collection, getDocs, doc, onSnapshot, query as fsQuery, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-hot-toast';

/**
 * Hook para gestionar datos del admin hub (OPTIMIZADO)
 * @param {Object} currentUser - Usuario actual de Firebase Auth
 * @param {boolean} isAdmin - Si el usuario es administrador
 * @param {boolean} authLoading - Si la autenticación está cargando
 * @returns {Object} Estados y datos del admin
 */
export const useAdminData = (currentUser, isAdmin, authLoading) => {
  const [students, setStudents] = useState([]);
  const [achievementsCatalog, setAchievementsCatalog] = useState([]);
  const [tutorsList, setTutorsList] = useState([]);  // NUEVO: Lista de tutores
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [studentData, setStudentData] = useState(null);
  const [briefings, setBriefings] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [allPayments, setAllPayments] = useState([]);

  // Cache refs para evitar recargas innecesarias
  const briefingsCache = useRef({});
  const unsubscribers = useRef({});

  // Carga inicial de datos de administrador (OPTIMIZADO)
  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    const fetchAdminData = async () => {
      try {
        // 🚀 OPTIMIZACIÓN 1: Cargar datos en paralelo, pero solo lo esencial
        const [studentsSnap, achievementsSnap, tutorsSnap] = await Promise.all([
          getDocs(collection(db, "students")),
          getDocs(collection(db, "achievements")),
          getDocs(collection(db, "tutors")),  // NUEVO: Cargar lista de tutores
        ]);

        // 🚀 OPTIMIZACIÓN 2: Solo guardar campos necesarios de estudiantes
        setStudents(
          studentsSnap.docs
            .map(doc => ({
              id: doc.id,
              firstName: doc.data().firstName,
              lastName: doc.data().lastName,
              email: doc.data().email,
              plan: doc.data().plan,
              level: doc.data().level,
              profileImageUrl: doc.data().profileImageUrl,
              userType: doc.data().userType || null,
              accountStatus: doc.data().accountStatus || null,
              // admin-reimaginado.md (2026-09-18): Hoy, Mentores y la lista de
              // Estudiantes necesitan saber quién es su mentor, su horario fijo,
              // su nivel del assessment y si el doc es de un mentor/admin. Sin
              // estos campos, Mentores contaba 0 estudiantes por mentor y Hoy
              // marcaba a todos "sin mentor" y "sin horario".
              tutorId: doc.data().tutorId || null,
              partnerIds: doc.data().partnerIds || [],
              availabilityRules: doc.data().availabilityRules || null,
              role: doc.data().role || null,
              currentLevel: doc.data().currentLevel || null,
              zoomLink: doc.data().zoomLink || null,
            }))
            .sort((a, b) => a.firstName.localeCompare(b.firstName))
        );

        setAchievementsCatalog(
          achievementsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        );

        // NUEVO: Cargar lista de tutores para el dropdown de asignación
        setTutorsList(
          tutorsSnap.docs
            .map(doc => ({
              uid: doc.id,
              displayName: doc.data().displayName || `${doc.data().firstName || ''} ${doc.data().lastName || ''}`.trim(),
              firstName: doc.data().firstName,
              lastName: doc.data().lastName,
              email: doc.data().email,
              isActive: doc.data().isActive !== false,  // Default true si no existe el campo
              is_flash_partner: doc.data().is_flash_partner === true,
              photo_url: doc.data().photo_url || doc.data().profileImageUrl || '',
            }))
            .filter(tutor => tutor.isActive)  // Solo tutores activos
            .sort((a, b) => (a.displayName || a.email).localeCompare(b.displayName || b.email))
        );

        // 🚀 OPTIMIZACIÓN 3: Cargar bookings y payments de forma LAZY
        // Solo cargar cuando sean realmente necesarios
        loadGlobalBookingsAndPayments();

      } catch (error) {
        console.error("Error fetching admin data:", error);
        toast.error("Failed to load initial admin data.");
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, [currentUser, isAdmin, authLoading]);

  // 🚀 OPTIMIZACIÓN 4: Función separada para cargar bookings/payments
  const loadGlobalBookingsAndPayments = async () => {
    try {
      // Cargar solo los últimos 100 de cada uno (suficiente para stats)
      const [bookingsSnap, paymentsSnap] = await Promise.all([
        getDocs(fsQuery(
          collection(db, "bookings"),
          orderBy("startTime", "desc"),
          limit(100)
        )),
        getDocs(fsQuery(
          collection(db, "payments"),
          orderBy("paymentDate", "desc"),
          limit(100)
        )),
      ]);

      setAllBookings(bookingsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setAllPayments(paymentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error("Error loading global bookings/payments:", error);
      // No mostrar error al usuario, los stats simplemente estarán vacíos
    }
  };

  // Listener en tiempo real para el estudiante seleccionado (OPTIMIZADO)
  useEffect(() => {
    if (!currentUser || authLoading) return;

    // 🚀 OPTIMIZACIÓN 5: Limpiar listeners anteriores
    if (unsubscribers.current.student) {
      unsubscribers.current.student();
    }
    if (unsubscribers.current.briefings) {
      unsubscribers.current.briefings();
    }

    if (!selectedStudent) {
      setStudentData(null);
      
      // Si NO hay estudiante seleccionado (nuevo workflow class-based), cargar briefings según rol
      // Si es admin: todos los briefings
      // Si es tutor: solo briefings de sus estudiantes
      if (isAdmin) {
        // Admin: cargar TODOS los briefings
        const allBriefingsQuery = fsQuery(
          collection(db, "session_briefings"),
          orderBy("createdAt", "desc"),
          limit(100)
        );
        
        unsubscribers.current.briefings = onSnapshot(allBriefingsQuery, (snapshot) => {
          const allBriefings = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setBriefings(allBriefings);
        });
      } else {
        // Tutor: cargar briefings de sus estudiantes con listeners en tiempo real
        const myStudentIds = students.map(s => s.id);
        if (myStudentIds.length > 0) {
          // Firestore limita "in" queries a 10 elementos, así que si hay más, hacemos queries múltiples
          const chunkSize = 10;
          const chunks = [];
          for (let i = 0; i < myStudentIds.length; i += chunkSize) {
            chunks.push(myStudentIds.slice(i, i + chunkSize));
          }
          
          // Map para combinar briefings de múltiples listeners
          const briefingsMap = new Map();
          const unsubscribeFunctions = [];
          
          // Crear listener para cada chunk
          chunks.forEach(chunk => {
            const chunkQuery = fsQuery(
              collection(db, "session_briefings"),
              where("studentId", "in", chunk),
              orderBy("createdAt", "desc"),
              limit(50)
            );
            
            const unsub = onSnapshot(chunkQuery, (snapshot) => {
              // Actualizar el map con los briefings de este chunk
              snapshot.docs.forEach(doc => {
                briefingsMap.set(doc.id, { id: doc.id, ...doc.data() });
              });
              
              // Convertir map a array y ordenar
              const allBriefings = Array.from(briefingsMap.values());
              allBriefings.sort((a, b) => {
                const dateA = a.createdAt?.toDate?.() || new Date(0);
                const dateB = b.createdAt?.toDate?.() || new Date(0);
                return dateB - dateA;
              });
              
              setBriefings(allBriefings);
            });
            
            unsubscribeFunctions.push(unsub);
          });
          
          // Guardar función para limpiar todos los listeners
          unsubscribers.current.briefings = () => {
            unsubscribeFunctions.forEach(unsub => unsub());
          };
        }
      }
      
      return;
    }

    // Listener de datos del estudiante
    const studentDocRef = doc(db, "students", selectedStudent);
    unsubscribers.current.student = onSnapshot(studentDocRef, (docSnap) => {
      setStudentData(docSnap.exists() ? docSnap.data() : null);
    });

    // 🚀 OPTIMIZACIÓN 6: Usar cache de briefings si existe
    if (briefingsCache.current[selectedStudent]) {
      setBriefings(briefingsCache.current[selectedStudent]);
    }

    // Listener de briefings (limitado a los últimos 20)
    const briefingsQuery = fsQuery(
      collection(db, "session_briefings"),
      where("studentId", "==", selectedStudent),
      orderBy("createdAt", "desc"),
      limit(20)
    );
    
    unsubscribers.current.briefings = onSnapshot(briefingsQuery, (snapshot) => {
      const newBriefings = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBriefings(newBriefings);
      // Guardar en cache
      briefingsCache.current[selectedStudent] = newBriefings;
    });

    // Cleanup function
    return () => {
      if (unsubscribers.current.student) {
        unsubscribers.current.student();
      }
      if (unsubscribers.current.briefings) {
        unsubscribers.current.briefings();
      }
    };
  }, [selectedStudent, currentUser, authLoading]);

  return {
    students,
    achievementsCatalog,
    tutorsList,          // NUEVO: Lista de tutores para asignación
    loading,
    selectedStudent,
    setSelectedStudent,
    studentData,
    briefings,
    allBookings,
    allPayments,
  };
};
